import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="co-scope min-h-screen flex items-center justify-center p-4" style={{ background: 'radial-gradient(1200px 600px at 50% -10%, #0a2233 0%, #041019 55%, #020b11 100%)' }}>
        <Card className="w-full max-w-md bg-[#081a25] border border-[#193b4b]">
          <CardContent className="p-8 text-center">
            <p className="text-red-600">Invalid reset link. Please request a new one.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/forgot-password')}>Request New Link</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="co-scope min-h-screen flex items-center justify-center p-4" style={{ background: 'radial-gradient(1200px 600px at 50% -10%, #0a2233 0%, #041019 55%, #020b11 100%)' }}>
      <Card className="w-full max-w-md bg-[#081a25] border border-[#193b4b]" data-testid="reset-password-page">
        <CardHeader className="text-center">
          <img src="/cipherone-logo.png" alt="CipherOne" className="w-12 h-12 object-contain mx-auto mb-2" />
          <CardTitle className="text-xl text-[#f4f8fa]">Set New Password</CardTitle>
          <CardDescription>Enter your new password below</CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4 py-4">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
              <p className="text-sm text-gray-700">Your password has been reset successfully.</p>
              <Button onClick={() => navigate('/login')} className="mt-4">Sign In</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} data-testid="new-password-input" />
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input type="password" placeholder="••••••••" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="confirm-password-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                <Lock className="h-4 w-4 mr-2" />
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
